import React, { useState } from 'react';
import FieldGroup from '@/components/form/FieldGroup/FieldGroup';
import Fieldset from '@/components/form/Fieldset/Fieldset';
import { useFormContext } from 'react-hook-form';
import Label from '@/components/form/Label/Label';
import SelectField from '@/components/form/SelectField/SelectField';
import TextField from '@/components/form/TextField/TextField';
import { useCrieSeuRoteiroContext } from '@/features/forms/CrieSeuRoteiro/context';
import dayjs from 'dayjs';
import MultiSelectControlled from '@/components/form/MultiSelect/MultiSelectControlled';
import Radio from '@/components/form/Radio/Radio';
import RadioGroup from '@/components/form/RadioGroup/RadioGroup';
import { FlexElement } from '@/features/forms/CrieSeuRoteiro/CrieSeuRoteiro.styles';
import { type FormCreateJourneyDataType } from '@/features/forms/CrieSeuRoteiro/validationScheme';
import { type ActionMeta } from 'react-select';
import { type SelectOptionType } from '@/components/form/MultiSelect/MultiSelect';
import { useTranslations } from 'next-intl';

const FormTravelData = () => {
  const t = useTranslations('FormCrieSeuRoteiro');

  const TRAVEL_BUDGET_OPTIONS = [
    { value: 'Entre 10.000 e 15.000', label: t('Entre {min} e {max}', { min: '10.000', max: '15.000' }) },
    { value: 'Entre 15.000 e 25.000', label: t('Entre {min} e {max}', { min: '15.000', max: '25.000' }) },
    { value: 'Entre 26.000 e 35.000', label: t('Entre {min} e {max}', { min: '26.000', max: '35.000' }) },
    { value: 'Entre 36.000 e 45.000', label: t('Entre {min} e {max}', { min: '36.000', max: '45.000' }) },
    { value: 'Acima de 46.000', label: t('Acima de {amount}', { amount: '46.000' }) },
  ];

  const TRAVEL_REASON_OPTIONS = [
    { value: 'Lua de Mel', label: t('Lua de Mel') },
    { value: 'Aniversário de casamento', label: t('Aniversário de casamento') },
    { value: 'Férias', label: t('Férias') },
    { value: 'Viagem de mergulho', label: t('Viagem de mergulho') },
    { value: 'Cruzeiro', label: t('Cruzeiro') },
    { value: 'Outros', label: t('Outros') },
  ];

  const TRAVEL_EXPERIENCIES_OPTIONS = [
    { value: 'Cultural', label: t('Cultural') },
    { value: 'Aventura', label: t('Aventura') },
    { value: 'Gastronômica', label: t('Gastronômica') },
    { value: 'Spa e tratamentos', label: t('Spa e tratamentos') },
    { value: 'Mergulho', label: t('Mergulho') },
    { value: 'Praia', label: t('Praia') },
    { value: 'Natureza', label: t('Natureza') },
    { value: 'Cidade', label: t('Cidade') },
    { value: 'Montanha', label: t('Montanha') },
    { value: 'Esqui', label: t('Esqui') },
  ];

  const {
    control,
    register,
    formState: { errors },
    ...methods
  } = useFormContext<FormCreateJourneyDataType>();

  const { watch } = methods;

  const { data } = useCrieSeuRoteiroContext();
  const { destinos, cidades } = data;
  const [cidadeOptions, setCidadeOptions] = useState<SelectTravelCitiesToVisitValue[]>(cidades);

  const handleOnSelect = (option: [SelectTravelDestinationValue], actionMeta: ActionMeta<SelectOptionType>) => {
    const destinoIds = option.map((i) => i.id);
    if (destinoIds.length) {
      setCidadeOptions((prevState) => [
        ...cidades.filter((c: SelectTravelCitiesToVisitValue) => destinoIds.includes(c.destino_id)),
      ]);
    } else {
      setCidadeOptions((prevState) => cidades);
    }
  };
  return (
    <>
      {/*<Banner show={Object.keys(errors).length > 0} appearance="error" size="large">*/}
      {/*  <Text align="center" weight="bold">*/}
      {/*    Por favor, corrigir o(s) erro(s) abaixo:*/}
      {/*  </Text>*/}
      {/*</Banner>*/}
      <Fieldset size="lg" align="center">
        <legend>{t('Informe os dados da sua viagem')}</legend>
        <FlexElement>
          <FieldGroup>
            <Label htmlFor="travelReason" required>
              {t('Motivo da viagem')}
            </Label>
            <SelectField id="travelReason" error={errors.travelReason?.message} {...register('travelReason')}>
              <option value="">-- {t('selecione')} --</option>
              {TRAVEL_REASON_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </SelectField>
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="travelPlannedDate" required>
              {t('Data prevista')}
            </Label>
            <TextField
              id="travelPlannedDate"
              type="date"
              error={errors.travelPlannedDate?.message}
              autoCapitalize="off"
              {...register('travelPlannedDate')}
              min={dayjs().add(7, 'days').format('YYYY-MM-DD')}
              max={dayjs().add(2, 'years').format('YYYY-MM-DD')}
            />
          </FieldGroup>
        </FlexElement>

        <FieldGroup>
          <Label htmlFor="travelDestinations" required>
            {t('Destino(s) que deseja visitar')}
          </Label>
          <MultiSelectControlled
            name="travelDestinations"
            // @ts-expect-error Invalid
            control={control}
            error={errors.travelDestinations?.message}
            onSelect={handleOnSelect}
            options={destinos.map((destino) => ({ label: destino.title, value: destino.title, id: destino.id }))}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="travelCitiesToVisit">{t('Cidade(s) que deseja visitar')}</Label>
          <MultiSelectControlled
            name="travelCitiesToVisit"
            // @ts-expect-error Invalid
            control={control}
            error={errors.travelCitiesToVisit?.message}
            options={cidadeOptions.map((cidade) => ({ label: cidade.title, value: cidade.title }))}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="travelExperiencies" required>
            {t('Experiências que lhe agradam')}
          </Label>
          <MultiSelectControlled
            name="travelExperiencies"
            // @ts-expect-error Invalid
            control={control}
            error={errors.travelExperiencies?.message}
            options={TRAVEL_EXPERIENCIES_OPTIONS}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="travelBudget" required>
            {t('Orçamento por pessoa em reais')} (R$)
          </Label>
          <SelectField id="travelBudget" error={errors.travelBudget?.message} {...register('travelBudget')}>
            <option value="">-- {t('selecione')} --</option>
            {TRAVEL_BUDGET_OPTIONS.filter((item, index) => {
              const items = watch('travelDestinations');
              const found = [...items].map((item) => item.value);
              if (index === 0) {
                return found.includes('Brasil');
              }
              return true;
            }).map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </SelectField>
        </FieldGroup>

        <FlexElement>
          <RadioGroup label={t('Você fala inglês?')}>
            <Radio label={t('Sim')} value="Sim" {...register('travelSpeakEng')} />
            <Radio label={t('Não')} value="Nāo" {...register('travelSpeakEng')} />
          </RadioGroup>
          <RadioGroup label={t('Idioma de preferência')} labelInfo={`(${t('sujeito à disponibilidade')})`}>
            <Radio label={t('Inglês')} value="Inglês" {...register('travelLangPref')} />
            <Radio label={t('Espanhol')} value="Espanhol" {...register('travelLangPref')} />
          </RadioGroup>
        </FlexElement>
      </Fieldset>
    </>
  );
};

export default FormTravelData;
